<template>
  <section class="mb-8">
    <!-- File Groups and DAS -->
    <template
      v-for="(section, index) in orderedSections"
      :key="`${section.type}-${index}`">
      <!-- File Groups -->
      <template v-if="section.sectionType === 'fileGroup'">
        <div class="mb-8">
          <h3
            class="font-roboto text-xl font-bold mb-2"
            :id="section.scrollId">
            {{ submissionFileTypeFormatter(section.data!.type) }}
          </h3>
          <ul
            class="space-y-4"
            role="list"
            :aria-label="`${submissionFileTypeFormatter(section.data!.type)} files`"
          >
            <li
              class="flex items-start bg-faint-grey rounded-lg p-4"
              v-for="(file, i) in section.data!.files"
              :key="i"
            >
              <div
                v-if="isFigure(file)"
                class="flex-shrink-0 mr-2">
                <div
                  :id="getFigureNumberId(section.data!.type, i)"
                  class="w-auto min-w-6 h-6 flex items-center justify-center rounded-full bg-royal-blue-10 text-base font-bold p-4"
                  :aria-label="`Figure ${i + 1}`"
                >
                  Fig.&nbsp;{{ i + 1 }}
                </div>
              </div>
              <div class="flex-1">
                <span :id="getFileNameId(section.data!.type, i)">
                  <strong
                    class="data-hj-suppress break-all"
                    data-testid="test-file-name-final-review">
                    {{ file.file }}
                  </strong>
                </span>
                <span :id="getFileSizeId(section.data!.type, i)">
                  ({{ bytesFormatter(file.size) }})
                </span>
              </div>
            </li>
          </ul>

          <!-- Conditional AddFileCard / Not provided -->
          <div v-if="section.data!.files.length === 0 && !['Esi', 'CrystalData', 'Figure'].includes(section.type)">
            <AddFileCard
              anchor-link="/submission/upload-files"
              :scroll-to-id="section.scrollId"
              :data-testid="getSectionProps(section.type)?.testId"
            />
            <InlineWarningMessage
              :errorMessage="getSectionProps(section.type)?.errorMessage"
              :errorTestId="getSectionProps(section.type)?.testId"
            />
          </div>

          <div
            v-else-if="section.data!.files.length === 0 && ['Esi', 'CrystalData', 'Figure'].includes(section.type)"
            class="bg-faint-grey rounded-md p-4 mb-4"
          >
            Not provided (optional)
          </div>
        </div>
      </template>

      <!-- DAS Section -->
      <template v-else-if="section.sectionType === 'das'">
        <section class="mb-8">
          <h3
            class="font-roboto text-xl font-bold mb-2"
            id="das-required">
            Data availability statement
          </h3>

          <AddFileCard
            v-if="!hasDASFiles"
            anchor-link="/submission/upload-files"
            scroll-to-id="das-required"
            data-testid="add-das-card"
          />
          <InlineWarningMessage
            v-if="dasErrorMessage"
            :errorMessage="dasErrorMessage" />

          <!-- Upload DAS -->
          <template v-if="dasSelectedOption === DasOption.File && hasDASFiles">
            <div class="bg-faint-grey rounded-lg p-4 mb-4">
              <span
                :id="getFileNameId(dasFile.type, 0)"
                class="font-bold data-hj-suppress break-all"
                data-testid="test-das-file-title-final-review"
              >
                {{ dasFile.file }}
              </span>
              <span :id="getFileSizeId(dasFile.type, 0)">
                ({{ bytesFormatter(dasFile.size) }})
              </span>
            </div>
          </template>

          <!-- Review DAS -->
          <template v-else-if="dasSelectedOption === DasOption.Review">
            <p class="mb-2">
              This manuscript has no primary data, the Data availability statement is:
            </p>
            <p
              id="editorReviewText"
              class="font-bold bg-faint-grey rounded-lg p-4 mb-4">
              'No primary research results, software or code have been included and no new data were generated or analysed as part of this review.'
            </p>
          </template>

          <!-- Custom DAS -->
          <template v-else-if="dasCustomIsSent">
            <p
              class="data-hj-suppress font-bold bg-faint-grey rounded-lg p-4 mb-4"
              :data-testid="showFullContent ? 'test-das-custom-full-final-review' : 'test-das-custom-not-full-final-review'"
              v-html="addIdToCustomDasText(showFullContent ? dasCustomContent : truncateByWords(dasCustomContent, 50))"
            >
            </p>
            <ButtonComponent
              :label="showFullContent ? 'View less' : 'View more'"
              variant="tertiary"
              @customClick="toggleContent"
              v-if="shouldShowToggle"
              data-testid="show-full-content-toggle"
              class="mt-3"
            />
          </template>
        </section>
      </template>
    </template>

    <!-- Peer Review Section -->
    <section>
      <div class="mb-8">
        <h3
          id="reviewTypeTitle"
          class="font-roboto text-xl mb-2 font-bold">
          Single-anonymised or double-anonymised peer review
        </h3>
        <p
          v-if="peerReviewChoice"
          class="bg-faint-grey rounded-lg p-4 mb-4"
          id="varReviewType">
          {{ peerReviewChoice }} {{ anonymisationDescription }}
        </p>
        <AddFileCard
          v-if="!peerReviewChoice"
          anchor-link="/submission/upload-files"
          scroll-to-id="reviewTypeTitle"
          data-testid="add-peer-review-card"
        />
        <InlineWarningMessage
          v-if="peerReviewAnonymisation === ReviewerAnonymisationType.Unknown"
          :errorMessage="reviewerAnonymisationErrorMessage"
        />
      </div>
    </section>

    <!-- Themed Collection -->
    <section>
      <h3
        class="font-roboto text-xl font-bold mb-2"
        id="themed-collection-option-required">
        Themed collection invitation
      </h3>
      <AddFileCard
        v-if="themedCollectionResponse === 'yes' && !themedCollectionTitle || themedCollectionResponse === ''"
        anchor-link="/submission/upload-files"
        scroll-to-id="themed-collection-option-required"
        data-testid="add-themed-collection-card"
      />
      <InlineWarningMessage
        v-if="themedCollectionErrorMessage"
        :errorMessage="themedCollectionErrorMessage" />
      <div v-if="themedCollectionShowValidResponse">
        <p
          v-if="themedCollectionResponse === 'yes'"
          class="mb-2">
          This manuscript is part of a themed collection. Title:
        </p>

        <div class="bg-faint-grey rounded-lg p-4 mb-4">
          <p
            data-testid="themed-collection-title-final-review"
            class="data-hj-suppress break-all">
            {{ themedCollectionResponse === 'yes' ? themedCollectionTitle : 'This manuscript is not part of a themed collection' }}
          </p>
        </div>
        <InlineWarningMessage
          v-if="themedCollectionMaxLengthErrorMessage"
          :errorMessage="themedCollectionMaxLengthErrorMessage"
        />
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { GroupedSubmissionFiles, SubmissionFile, SubmissionFileType, ReviewerAnonymisationType, DasOption } from '@/types'
import { useStore } from 'vuex'
import { submissionFileTypeFormatter, bytesFormatter } from '../../services/formatters'
import ButtonComponent from '../ui-components/ButtonComponent.vue'
import { useField } from 'vee-validate'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import AddFileCard from '../common/AddFileCard.vue'

interface Section {
  type: SubmissionFileType | 'DAS'
  sectionType: 'fileGroup' | 'das'
  data: GroupedSubmissionFiles | null
  order: number
  scrollId: string
  id: string
}

const store = useStore()
const groupedFiles = store.getters.groupedSubmissionFiles as GroupedSubmissionFiles[]
const dasSelectedOption = store.state.das.selectedOption
const dasCustomContent = store.state.das.dasCustomContent
const dasCustomIsSent = store.state.das.dasCustomIsSent
const hasDASFiles = groupedFiles.some(fileGroup => fileGroup.type === 'DAS')
const dasFile = store.state.activeSubmission.files.find(
  (file: { type: SubmissionFileType }) => file.type === SubmissionFileType.DAS,
) as SubmissionFile
const showFullContent = ref(false)
const themedCollectionResponse = store.state.activeSubmission.themedCollectionResponse
const themedCollectionTitle = store.state.activeSubmission.themedCollectionTitle
const themedCollectionShowValidResponse = computed(
  () => themedCollectionResponse === 'no' || (themedCollectionResponse === 'yes' && themedCollectionTitle !== ''),
)

function isFigure(file: SubmissionFile): boolean {
  return file.type === SubmissionFileType.Figure
}
function getFileNameId(type: string, index: number): string {
  return 'file' + type + 'Id-' + index
}
function getFileSizeId(type: string, index: number): string {
  return 'fileSize' + type + 'Id-' + index
}
function getFigureNumberId(type: string, index: number): string {
  return 'fileFigureNumber' + type + 'Id-' + index
}
function truncateByWords(text: string, limit: number): string {
  const words = text.split(' ')
  return words.length > limit ? words.slice(0, limit).join(' ') + '...' : text
}
function addIdToCustomDasText(htmlContent: string) {
  return htmlContent.replace('<p>', '<p id="customDasText">')
}
function toggleContent() {
  showFullContent.value = !showFullContent.value
}
const shouldShowToggle = computed(() => dasCustomContent.split(' ').length > 50)

const sectionOrder = [
  SubmissionFileType.MainArticle,
  SubmissionFileType.CoverLetter,
  SubmissionFileType.DAS,
  SubmissionFileType.Esi,
  SubmissionFileType.CrystalData,
  SubmissionFileType.Figure,
]
const sectionOrderMap = Object.fromEntries(sectionOrder.map((type, index) => [type, index]))

function getScrollId(type: SubmissionFileType | 'DAS'): string {
  switch (type) {
    case SubmissionFileType.MainArticle: return 'required-manuscript'
    case SubmissionFileType.CoverLetter: return 'required-cover-letter'
    case 'DAS': return 'das-required'
    default: return type.toString()
  }
}

const orderedSections = computed<Section[]>(() => {
  const sections: Section[] = []

  const allFileTypes: SubmissionFileType[] = [
    SubmissionFileType.MainArticle,
    SubmissionFileType.CoverLetter,
    SubmissionFileType.Esi,
    SubmissionFileType.CrystalData,
    SubmissionFileType.Figure,
  ]

  const fileGroups = allFileTypes.map((type) => {
    const existing = groupedFiles.find(g => g.type === type)
    return {
      type,
      sectionType: 'fileGroup' as const,
      data: existing ?? { type, files: [] },
      order: sectionOrderMap[type] ?? 999,
      scrollId: getScrollId(type),
      id: `section-${type}`,
    }
  })

  sections.push(...fileGroups)
  sections.push({ type: 'DAS', sectionType: 'das' as const, data: null, order: sectionOrderMap['DAS'] ?? 3, scrollId: 'das-required', id: 'section-DAS' })
  return sections.sort((a, b) => a.order - b.order)
})

const filteredGroupedFiles = computed(() =>
  orderedSections.value.filter(section => section.sectionType === 'fileGroup' && section.data !== null).map(section => section.data!),
)

const peerReviewAnonymisation = computed((): ReviewerAnonymisationType => store.state.activeSubmission.peerReviewAnonymisation)
const peerReviewChoice = computed((): string => {
  if (peerReviewAnonymisation.value === ReviewerAnonymisationType.SingleAnonymised) return 'Single-Anonymised'
  else if (peerReviewAnonymisation.value === ReviewerAnonymisationType.DoubleAnonymised) return 'Double-Anonymised'
  return ''
})
const anonymisationDescription = computed((): string => {
  if (peerReviewAnonymisation.value === ReviewerAnonymisationType.SingleAnonymised)
    return '(reviewers are anonymous but author names and affiliations are known to reviewers)'
  else if (peerReviewAnonymisation.value === ReviewerAnonymisationType.DoubleAnonymised)
    return '(authors\' and reviewers\' identities are concealed from each other)'
  return ''
})

const { errorMessage: manuscriptErrorMessage, validate: validateManuscript } = useField(
  'input-filtered-grouped-files', 'manuscript-file-required', { initialValue: filteredGroupedFiles },
)
const { errorMessage: coverLetterErrorMessage, validate: validateCoverLetter } = useField(
  'input-filtered-grouped-files', 'cover-letter-required', { initialValue: filteredGroupedFiles },
)
const { errorMessage: themedCollectionErrorMessage, validate: validateThemedCollection } = useField(
  'input-themed-collection', 'themed-collection-option-required', { initialValue: null },
)
const { errorMessage: reviewerAnonymisationErrorMessage, validate: validateReviewerAnonymisation }
  = useField('input-peer-review-anonymisation', 'anonymised-peer-review-option-required', { initialValue: null })

const dasFiles = computed(() => groupedFiles.find(fileGroup => fileGroup.type === 'DAS')?.files ?? [])
const { errorMessage: dasErrorMessage, validate: validateDas } = useField(
  'input-data-availability-statement', 'data-availability-statement-option-required', { initialValue: dasFiles.value },
)

const { errorMessage: themedCollectionMaxLengthErrorMessage, validate: validateThemedCollectionMaxLength } = useField(
  'input-themed-collection-title', 'themed-collection-max-length', { initialValue: themedCollectionTitle },
)

function getSectionProps(
  type: Section['type'],
): { errorMessage: string, testId: string } | undefined {
  switch (type) {
    case SubmissionFileType.MainArticle:
      return {
        errorMessage: manuscriptErrorMessage.value ?? '',
        testId: 'add-manuscript-card',
      }
    case SubmissionFileType.CoverLetter:
      return {
        errorMessage: coverLetterErrorMessage.value ?? '',
        testId: 'add-cover-letter-card',
      }
    case 'DAS':
      return {
        errorMessage: dasErrorMessage.value ?? '',
        testId: 'add-das-card',
      }
  }
}

watch(dasFiles, () => validateDas())
watch(() => store.state.submissionAttemptCounter, () => {
  validateManuscript()
  validateCoverLetter()
  validateThemedCollection()
  validateReviewerAnonymisation()
  validateDas()
  validateThemedCollectionMaxLength()
})
</script>
